import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

import { ApiError } from '../../shared/api/client.ts';
import { useBaixarXml, useXmlDoArquivo } from '../../shared/api/queries.ts';
import type { ArquivoFiscal } from '../../shared/api/tipos.ts';
import {
  formatarChaveAcesso,
  formatarCnpj,
  formatarDataHora,
  formatarMoedaDeCentavos,
  formatarQuantidade,
} from '../../shared/format.ts';

/**
 * O cupom, desenhado a partir do XML guardado.
 *
 * É a leitura humana da nota: quem abre aqui quer conferir o que foi vendido,
 * não ler XML. O desenho imita o cupom que saiu na impressora da loja — coluna
 * estreita, fonte monoespaçada, itens, totais e pagamento — porque é essa a
 * folha que o cliente tem na mão quando liga para o escritório.
 */
export function DialogoCupom({
  arquivo,
  aoFechar,
}: {
  arquivo: ArquivoFiscal | null;
  aoFechar: () => void;
}) {
  const xml = useXmlDoArquivo(arquivo?.id ?? null);
  const baixarXml = useBaixarXml();

  const nota = xml.data === undefined ? null : lerNota(xml.data);

  return (
    <Dialog open={arquivo !== null} onClose={aoFechar} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle>
        {arquivo?.modelo === '65' ? 'Cupom fiscal eletrônico' : 'Nota fiscal eletrônica'}
        <Typography variant="body2" color="text.secondary">
          nº {arquivo?.numero} · série {Number(arquivo?.serie ?? 0)}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {xml.isPending ? (
          <Stack spacing={1}>
            {Array.from({ length: 8 }, (_, indice) => (
              <Skeleton key={indice} variant="text" />
            ))}
          </Stack>
        ) : xml.isError ? (
          <Alert severity="error">
            Não foi possível abrir o XML desta nota. {mensagemDoErro(xml.error)}
          </Alert>
        ) : nota === null ? (
          <Alert severity="warning">
            O arquivo guardado não tem a forma de uma NF-e — não dá para desenhar o cupom.
            Baixe o XML para conferir.
          </Alert>
        ) : (
          <Cupom nota={nota} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          startIcon={<DownloadOutlinedIcon />}
          onClick={() => {
            if (arquivo !== null) baixarXml.mutate(arquivo.id);
          }}
          loading={baixarXml.isPending}
        >
          Baixar XML
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" onClick={aoFechar}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Cupom({ nota }: { nota: NotaLida }) {
  return (
    <Box sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', maxWidth: 440, mx: 'auto' }}>
      <Stack spacing={0.25} sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontFamily: 'inherit', fontWeight: 700 }}>
          {nota.emitente.nome}
        </Typography>
        {nota.emitente.fantasia !== '' && nota.emitente.fantasia !== nota.emitente.nome && (
          <Typography sx={{ fontFamily: 'inherit' }}>{nota.emitente.fantasia}</Typography>
        )}
        <Typography sx={{ fontFamily: 'inherit' }}>
          CNPJ {formatarCnpj(nota.emitente.cnpj)}
        </Typography>
        {nota.emitente.endereco !== '' && (
          <Typography sx={{ fontFamily: 'inherit' }}>{nota.emitente.endereco}</Typography>
        )}
      </Stack>

      <Linha />

      <Typography sx={{ fontFamily: 'inherit', textAlign: 'center' }}>
        {nota.modelo === '65' ? 'DANFE NFC-e — Documento Auxiliar' : 'DANFE NF-e'}
        {nota.homologacao && ' · AMBIENTE DE HOMOLOGAÇÃO'}
      </Typography>

      <Linha />

      {/* Cabeçalho da lista de itens: as mesmas colunas do cupom impresso. */}
      <Grade
        celulas={['Cód · Descrição', 'Qtd', 'Un.', 'Valor']}
        cabecalho
      />
      {nota.itens.map((item, indice) => (
        <Box key={`${item.codigo}-${indice}`} sx={{ mb: 0.75 }}>
          <Typography sx={{ fontFamily: 'inherit' }}>
            {item.codigo} {item.descricao}
          </Typography>
          <Grade
            celulas={[
              '',
              formatarQuantidade(item.quantidade),
              formatarMoedaDeCentavos(item.unitarioCentavos),
              formatarMoedaDeCentavos(item.totalCentavos),
            ]}
          />
        </Box>
      ))}

      <Linha />

      <Grade celulas={['Itens', '', '', String(nota.itens.length)]} />
      {nota.descontoCentavos > 0 && (
        <Grade
          celulas={['Descontos', '', '', `- ${formatarMoedaDeCentavos(nota.descontoCentavos)}`]}
        />
      )}
      <Grade
        celulas={['TOTAL', '', '', formatarMoedaDeCentavos(nota.totalCentavos)]}
        destaque
      />

      {nota.pagamentos.length > 0 && (
        <>
          <Linha />
          {nota.pagamentos.map((pagamento, indice) => (
            <Grade
              key={indice}
              celulas={[pagamento.forma, '', '', formatarMoedaDeCentavos(pagamento.valorCentavos)]}
            />
          ))}
          {nota.trocoCentavos > 0 && (
            <Grade celulas={['Troco', '', '', formatarMoedaDeCentavos(nota.trocoCentavos)]} />
          )}
        </>
      )}

      <Linha />

      <Stack spacing={0.25}>
        {nota.destinatario !== '' && (
          <Typography sx={{ fontFamily: 'inherit' }}>Consumidor: {nota.destinatario}</Typography>
        )}
        {nota.emitidoEm !== '' && (
          <Typography sx={{ fontFamily: 'inherit' }}>
            Emissão: {formatarDataHora(nota.emitidoEm)}
          </Typography>
        )}
        <Typography sx={{ fontFamily: 'inherit', wordBreak: 'break-all' }}>
          Chave: {formatarChaveAcesso(nota.chave)}
        </Typography>
        {nota.protocolo !== '' && (
          <Typography sx={{ fontFamily: 'inherit' }}>Protocolo: {nota.protocolo}</Typography>
        )}
      </Stack>
    </Box>
  );
}

function Linha() {
  return <Divider sx={{ my: 1, borderStyle: 'dashed' }} />;
}

function Grade({
  celulas,
  cabecalho = false,
  destaque = false,
}: {
  celulas: readonly string[];
  cabecalho?: boolean;
  destaque?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 4.5rem 5.5rem 6rem',
        columnGap: 1,
        color: cabecalho ? 'text.secondary' : undefined,
        fontWeight: destaque ? 700 : undefined,
        fontSize: destaque ? '1rem' : undefined,
      }}
    >
      {celulas.map((celula, indice) => (
        <Box
          key={indice}
          sx={{
            fontFamily: 'inherit',
            textAlign: indice === 0 ? 'left' : 'right',
            whiteSpace: 'nowrap',
          }}
        >
          {celula}
        </Box>
      ))}
    </Box>
  );
}

interface ItemLido {
  codigo: string;
  descricao: string;
  quantidade: number;
  unitarioCentavos: number;
  totalCentavos: number;
}

interface NotaLida {
  modelo: string;
  homologacao: boolean;
  emitente: { nome: string; fantasia: string; cnpj: string; endereco: string };
  destinatario: string;
  itens: ItemLido[];
  descontoCentavos: number;
  totalCentavos: number;
  trocoCentavos: number;
  pagamentos: Array<{ forma: string; valorCentavos: number }>;
  emitidoEm: string;
  chave: string;
  protocolo: string;
}

/**
 * Formas de pagamento da NF-e (tabela `tPag`). Só as que aparecem num PDV de
 * loja; o resto cai no rótulo genérico, que é melhor do que mostrar o código.
 */
const FORMAS_DE_PAGAMENTO: Record<string, string> = {
  '01': 'Dinheiro',
  '02': 'Cheque',
  '03': 'Cartão de crédito',
  '04': 'Cartão de débito',
  '05': 'Crédito da loja',
  '10': 'Vale alimentação',
  '11': 'Vale refeição',
  '12': 'Vale presente',
  '13': 'Vale combustível',
  '15': 'Boleto',
  '16': 'Depósito',
  '17': 'Pix',
  '18': 'Transferência',
  '19': 'Cashback',
  '90': 'Sem pagamento',
  '99': 'Outros',
};

/**
 * Lê o XML. Devolve `null` quando o arquivo não é uma NF-e — o conteúdo vem do
 * emissor do cliente, e um arquivo truncado ou de outro layout não pode virar
 * tela quebrada.
 */
function lerNota(xml: string): NotaLida | null {
  const documento = new DOMParser().parseFromString(xml, 'application/xml');
  if (documento.getElementsByTagName('parsererror').length > 0) return null;

  const infNFe = primeiro(documento, 'infNFe');
  const ide = primeiro(documento, 'ide');
  const emit = primeiro(documento, 'emit');
  if (infNFe === null || ide === null || emit === null) return null;

  // A chave vem no atributo `Id`, prefixada por "NFe".
  const chave = (infNFe.getAttribute('Id') ?? '').replace(/\D/g, '');

  const enderecoEmitente = primeiro(emit, 'enderEmit');
  const endereco =
    enderecoEmitente === null
      ? ''
      : [
          [texto(enderecoEmitente, 'xLgr'), texto(enderecoEmitente, 'nro')]
            .filter((parte) => parte !== '')
            .join(', '),
          texto(enderecoEmitente, 'xBairro'),
          [texto(enderecoEmitente, 'xMun'), texto(enderecoEmitente, 'UF')]
            .filter((parte) => parte !== '')
            .join('/'),
        ]
          .filter((parte) => parte !== '')
          .join(' · ');

  const itens = [...documento.getElementsByTagName('det')].flatMap((det) => {
    const prod = primeiro(det, 'prod');
    if (prod === null) return [];

    return [
      {
        codigo: texto(prod, 'cProd'),
        descricao: texto(prod, 'xProd'),
        quantidade: numero(texto(prod, 'qCom')),
        unitarioCentavos: centavos(texto(prod, 'vUnCom')),
        totalCentavos: centavos(texto(prod, 'vProd')),
      },
    ];
  });

  const total = primeiro(documento, 'ICMSTot');
  const dest = primeiro(documento, 'dest');

  const pagamentos = [...documento.getElementsByTagName('detPag')].map((detPag) => ({
    forma: FORMAS_DE_PAGAMENTO[texto(detPag, 'tPag')] ?? 'Pagamento',
    valorCentavos: centavos(texto(detPag, 'vPag')),
  }));

  const protocolo = primeiro(documento, 'infProt');

  return {
    modelo: texto(ide, 'mod'),
    homologacao: texto(ide, 'tpAmb') === '2',
    emitente: {
      nome: texto(emit, 'xNome'),
      fantasia: texto(emit, 'xFant'),
      cnpj: texto(emit, 'CNPJ'),
      endereco,
    },
    destinatario:
      dest === null
        ? ''
        : [texto(dest, 'xNome'), texto(dest, 'CPF') || texto(dest, 'CNPJ')]
            .filter((parte) => parte !== '')
            .join(' · '),
    itens,
    descontoCentavos: total === null ? 0 : centavos(texto(total, 'vDesc')),
    totalCentavos:
      total === null
        ? itens.reduce((soma, item) => soma + item.totalCentavos, 0)
        : centavos(texto(total, 'vNF')),
    trocoCentavos: centavos(texto(documento, 'vTroco')),
    pagamentos,
    emitidoEm: instanteDeEmissao(texto(ide, 'dhEmi') || texto(ide, 'dEmi')),
    chave,
    protocolo: protocolo === null ? '' : descreverProtocolo(protocolo),
  };
}

/** Número do protocolo e quando a SEFAZ autorizou, já em data e hora. */
function descreverProtocolo(protocolo: Element): string {
  const numero = texto(protocolo, 'nProt');
  const recebido = instanteDeEmissao(texto(protocolo, 'dhRecbto'));

  return [numero, recebido === '' ? '' : formatarDataHora(recebido)]
    .filter((parte) => parte !== '')
    .join(' · ');
}

/**
 * NF-e antiga traz só a data (`dEmi`), sem hora nem fuso. Lida como está, ela
 * vira meia-noite em UTC e aparece como o dia anterior no horário de Brasília
 * — a nota do dia 1º apareceria no mês passado.
 */
function instanteDeEmissao(valor: string): string {
  if (valor === '') return '';
  return valor.includes('T') ? valor : `${valor}T00:00:00-03:00`;
}

function primeiro(raiz: Document | Element, nome: string): Element | null {
  return raiz.getElementsByTagName(nome).item(0);
}

function texto(raiz: Document | Element, nome: string): string {
  return primeiro(raiz, nome)?.textContent?.trim() ?? '';
}

function numero(valor: string): number {
  const convertido = Number(valor);
  return Number.isFinite(convertido) ? convertido : 0;
}

/**
 * O XML traz reais com casas decimais; o resto do sistema trabalha em centavos
 * inteiros. `Math.round` é o que evita `0.1 + 0.2` virar um centavo a menos no
 * total.
 */
function centavos(valor: string): number {
  return Math.round(numero(valor) * 100);
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) return erro.message;
  return erro instanceof Error ? erro.message : 'Falha inesperada.';
}
